/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, DoorOpen, Stethoscope, Cpu, Power } from 'lucide-react';
import logger from '@/lib/logger';
import { TagInput } from '@/components/TagInput';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { nome: '', numero: '', descrizione: '', attrezzature: [], medici_ids: [] };

export default function GestioneStudi() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [studios, setStudios] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        axios.get(`${API}/studios?active_only=false`),
        axios.get(`${API}/doctors?active_only=false`),
      ]);
      setStudios(sRes.data);
      setDoctors(dRes.data);
      try {
        const tRes = await axios.get(`${API}/equipment-tags`);
        setTagSuggestions(tRes.data.tags || []);
      } catch (e) { logger.warn('Tag suggestions (opzionali) non caricati:', e?.message); }
    } catch (err) {
      logger.error('Fetch error:', err);
      toast.error('Errore nel caricamento degli studi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doctorName = (id) => {
    const d = doctors.find(x => x.id === id);
    return d ? `Dott. ${d.nome} ${d.cognome}` : null;
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({
      nome: s.nome || '',
      numero: s.numero || '',
      descrizione: s.descrizione || '',
      attrezzature: s.attrezzature || [],
      medici_ids: s.medici_ids || [],
    });
    setDialogOpen(true);
  };

  const toggleMedico = (id) => {
    setForm(p => ({
      ...p,
      medici_ids: p.medici_ids.includes(id)
        ? p.medici_ids.filter(x => x !== id)
        : [...p.medici_ids, id],
    }));
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) { toast.error('Il nome dello studio è obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form, nome: form.nome.trim(), numero: form.numero.trim() || null };
      if (editing) {
        await axios.put(`${API}/studios/${editing}`, payload);
        toast.success('Studio aggiornato');
      } else {
        await axios.post(`${API}/studios`, { ...payload, active: true });
        toast.success('Studio creato');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/studios/${id}`);
      toast.success('Studio disattivato');
      fetchData();
    } catch {
      toast.error('Errore nella disattivazione');
    }
  };

  const handleReactivate = async (s) => {
    try {
      await axios.put(`${API}/studios/${s.id}`, { active: true });
      toast.success(`Studio "${s.nome}" riattivato`);
      fetchData();
    } catch {
      toast.error('Errore nella riattivazione');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Gestione Studi" info="Le stanze/ambulatori della sede dove operano i medici. Uno studio disattivato non è assegnabile alle disponibilità." />
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="studio-create-button">
            <Plus size={16} className="mr-1" /> Nuovo Studio
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : studios.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground" data-testid="studi-empty-state">
              Nessuno studio presente. {isAdmin ? 'Crea il primo con "Nuovo Studio".' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Numero</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Attrezzature</TableHead>
                    <TableHead>Medici abituali</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {studios.map(s => (
                    <TableRow key={s.id} data-testid="studio-row">
                      <TableCell className="font-semibold">
                        {s.numero ? <Badge variant="outline">{s.numero}</Badge> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <DoorOpen size={15} className="text-[#238A92]" /> {s.nome}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        {s.attrezzature && s.attrezzature.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {s.attrezzature.slice(0, 4).map(t => (
                              <Badge key={t} variant="outline" className="text-[11px] font-normal">{t}</Badge>
                            ))}
                            {s.attrezzature.length > 4 && <Badge variant="outline" className="text-[11px]">+{s.attrezzature.length - 4}</Badge>}
                          </div>
                        ) : <span className="text-muted-foreground text-sm">—</span>}
                      </TableCell>
                      <TableCell>
                        {s.medici_ids && s.medici_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {s.medici_ids.slice(0, 2).map(id => {
                              const n = doctorName(id);
                              return n ? <Badge key={id} variant="secondary" className="text-[11px]">{n}</Badge> : null;
                            })}
                            {s.medici_ids.length > 2 && <Badge variant="secondary" className="text-[11px]">+{s.medici_ids.length - 2}</Badge>}
                          </div>
                        ) : <span className="text-muted-foreground text-sm">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={s.active ? 'default' : 'secondary'} className={s.active ? 'bg-green-100 text-green-800' : ''}>
                          {s.active ? 'Attivo' : 'Inattivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEdit(s)} data-testid="studio-edit-button"><Edit size={14} /></Button>
                            {s.active ? (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" title="Disattiva studio" data-testid={`studio-deactivate-btn-${s.id}`}><Trash2 size={14} /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Disattiva Studio</AlertDialogTitle>
                                    <AlertDialogDescription>Sei sicuro di voler disattivare "{s.nome}"? Non sarà più assegnabile a nuovi appuntamenti.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(s.id)} className="bg-red-600 hover:bg-red-700">Disattiva</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            ) : (
                              <Button size="sm" variant="outline" className="h-8 px-2 text-[#1E7A58] border-[#1E7A58]/40 hover:bg-[#1E7A58]/10" onClick={() => handleReactivate(s)} title="Riattiva studio" data-testid={`studio-reactivate-btn-${s.id}`}>
                                <Power size={14} className="mr-1" /> Riattiva
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>{editing ? 'Modifica Studio' : 'Nuovo Studio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Numero stanza</Label>
                <Input value={form.numero} onChange={e => setForm(p => ({ ...p, numero: e.target.value }))} placeholder="es. 1" data-testid="studio-numero-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Nome <span className="text-red-500">*</span></Label>
                <Input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="es. Ambulatorio Cardiologia" data-testid="studio-nome-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={form.descrizione} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} rows={2} placeholder="Breve descrizione dello studio" data-testid="studio-descrizione-input" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Cpu size={14} className="text-[#238A92]" /> Attrezzature (tag)</Label>
              <p className="text-xs text-muted-foreground">Macchinari e dotazioni presenti nello studio. Il sistema li usa per suggerire gli studi compatibili con le esigenze di ogni medico.</p>
              <TagInput
                value={form.attrezzature}
                onChange={(tags) => setForm(p => ({ ...p, attrezzature: tags }))}
                suggestions={tagSuggestions}
                placeholder="es. ECG, Ecografo, Lettino..."
                testId="studio-attrezzature-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Stethoscope size={14} className="text-[#238A92]" /> Medici abituali</Label>
              <p className="text-xs text-muted-foreground">Elenco informativo dei medici che usano abitualmente questo studio.</p>
              <ScrollArea className="h-44 border rounded-lg p-3">
                {doctors.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dottore disponibile.</p>
                ) : (
                  <div className="space-y-2">
                    {doctors.map(d => (
                      <label key={d.id} htmlFor={`med-${d.id}`} className="flex items-center gap-2 cursor-pointer text-sm py-0.5">
                        <Checkbox id={`med-${d.id}`} checked={form.medici_ids.includes(d.id)} onCheckedChange={() => toggleMedico(d.id)} data-testid={`studio-medico-${d.id}`} />
                        <span>Dott. {d.nome} {d.cognome}</span>
                        <span className="text-xs text-muted-foreground">— {d.specializzazione}</span>
                      </label>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="studio-save-button">
              {saving ? 'Salvataggio...' : (editing ? 'Salva' : 'Crea')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
